import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  androidStores,
  app,
  appStoreSearchUrl,
  assetUrl,
  hasAppStoreId,
  iosStore,
  officialDownloadUrl,
  SITE_HOST,
  storeWebUrl,
} from '../config.js';
import BrowserGuide from '../components/BrowserGuide.jsx';
import ShareCard from '../components/ShareCard.jsx';
import StoreGrid from '../components/StoreGrid.jsx';
import Toast, { useToast } from '../components/Toast.jsx';
import { copyText } from '../lib/clipboard.js';
import { buildAppLink, openStore, tryOpenApp } from '../lib/deeplink.js';
import {
  detectBrowser,
  getPreferredStore,
  isAndroid,
  isDesktop,
  isIOS,
  isMobile,
} from '../lib/platform.js';

/**
 * 首页 = 下载引导页。
 *
 * 被分享链接「拦截」过来的用户，以及直接访问域名的用户，都落在这里。
 * 因为 App 还没上架，这里的商店链接用的是「按包名直达本机商店」的协议，
 * 上架后同一个链接就会打开详情页，不需要改代码。
 */
export default function HomePage() {
  const location = useLocation();
  const [toast, showToast] = useToast();

  // 从 SharePage 重定向过来时带着「为什么来」和「原本想看什么」
  const { reason = null, share = null } = location.state ?? {};

  const browser = useMemo(() => detectBrowser(), []);
  const preferredStore = useMemo(() => getPreferredStore(androidStores), []);

  const desktop = isDesktop();
  const ios = isIOS();
  const android = isAndroid();

  // 微信/QQ 里一进来就提示换浏览器：不提示的话后面所有下载按钮都会静默失败
  const [guideOpen, setGuideOpen] = useState(browser.blocksScheme && isMobile());

  const appStoreUrl = hasAppStoreId && iosStore ? storeWebUrl(iosStore) : appStoreSearchUrl;

  /** 点商店：桌面端直接开网页版，手机端优先唤起本机商店。 */
  const handleOpenStore = async (store) => {
    if (desktop) {
      const webUrl = storeWebUrl(store);
      if (webUrl) {
        window.open(webUrl, '_blank', 'noopener,noreferrer');
      } else {
        showToast(`「${store.name}」没有网页版，请用手机打开`);
      }
      return;
    }

    const outcome = await openStore(store);
    if (outcome === 'unavailable') {
      showToast(`当前环境打不开「${store.name}」，换个商店试试`);
    }
  };

  const handleCopyLink = async () => {
    const ok = await copyText(window.location.href);
    showToast(ok ? '链接已复制，粘贴到手机浏览器打开' : '复制失败，请长按地址栏手动复制');
  };

  /**
   * 「我已经装好了」：重新尝试唤起 App。
   *
   * 这里必须把 share.search 一起带上：被拦到首页的用户多半是从商品 / 优惠券
   * 链接过来的，丢掉 ?shopId= 就会唤起到一个「缺少商品或店铺参数」的错误页 ——
   * 也就是在用户装好 App 之后又失败一次，比不提供这个按钮还糟。
   */
  const handleRetryOpen = () => {
    if (!share?.path) return;

    const search = share.search || '';

    tryOpenApp({
      url: buildAppLink(share.path, search),
      key: `home:${share.path}${search}:${Date.now()}`,
    }).then((outcome) => {
      if (outcome !== 'opened') {
        showToast('还是没有检测到 App，先把 App 装上吧');
      }
    });
  };

  /* ---------------------------------------------------------------- */
  /* 提示条                                                           */
  /* ---------------------------------------------------------------- */

  let notice = null;

  if (reason === 'not-installed') {
    notice = (
      <div className="notice notice--warn">
        <span className="notice__icon" aria-hidden="true">
          !
        </span>
        <div className="notice__body">
          <span className="notice__title">这台手机还没安装「{app.name}」</span>
          <p className="notice__text">
            安装后重新打开分享链接，就能直接跳到 App 里对应的页面。
          </p>
          {share?.path ? (
            <button type="button" className="btn btn--link" onClick={handleRetryOpen}>
              我已经装好了，再试一次
            </button>
          ) : null}
        </div>
      </div>
    );
  } else if (reason === 'desktop') {
    notice = (
      <div className="notice notice--info">
        <span className="notice__icon" aria-hidden="true">
          i
        </span>
        <div className="notice__body">
          <span className="notice__title">你正在电脑上打开分享链接</span>
          <p className="notice__text">
            App 只能在手机上唤起。把下面的链接发到手机上打开，或直接复制到手机浏览器。
          </p>
        </div>
      </div>
    );
  } else if (reason === 'invalid') {
    notice = (
      <div className="notice notice--warn">
        <span className="notice__icon" aria-hidden="true">
          !
        </span>
        <div className="notice__body">
          <span className="notice__title">分享链接已失效</span>
          <p className="notice__text">链接可能不完整或已被修改，可以在这里直接下载 App。</p>
        </div>
      </div>
    );
  } else if (browser.blocksScheme && isMobile()) {
    notice = (
      <div className="notice notice--info">
        <span className="notice__icon" aria-hidden="true">
          i
        </span>
        <div className="notice__body">
          <span className="notice__title">建议用手机浏览器打开</span>
          <p className="notice__text">
            「{browser.name}」会拦截 App 唤起和商店跳转，换到 Safari / Chrome 会更顺。
          </p>
          <button type="button" className="btn btn--link" onClick={() => setGuideOpen(true)}>
            看看怎么换
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* 主按钮                                                           */
  /* ---------------------------------------------------------------- */

  let primaryAction = null;

  if (desktop) {
    primaryAction = (
      <button type="button" className="btn btn--primary" onClick={handleCopyLink}>
        复制链接，到手机上打开
        <span className="btn__hint">支持 iPhone / Android</span>
      </button>
    );
  } else if (ios) {
    primaryAction = (
      <a className="btn btn--primary" href={appStoreUrl}>
        {hasAppStoreId ? '从 App Store 下载' : '去 App Store 搜索'}
        <span className="btn__hint">{hasAppStoreId ? 'iOS' : app.name}</span>
      </a>
    );
  } else if (officialDownloadUrl) {
    primaryAction = (
      <a className="btn btn--primary" href={officialDownloadUrl}>
        下载「{app.name}」
        <span className="btn__hint">官方下载</span>
      </a>
    );
  } else if (preferredStore) {
    primaryAction = (
      <button type="button" className="btn btn--primary" onClick={() => handleOpenStore(preferredStore)}>
        从「{preferredStore.name}」下载
        {share?.path ? <span className="btn__hint">安装后自动打开分享内容</span> : null}
      </button>
    );
  }

  // iOS 上没必要列一堆安卓商店；桌面端和安卓端才展示商店网格
  const gridItems = desktop
    ? [...(iosStore ? [iosStore] : []), ...androidStores]
    : android
      ? androidStores
      : [];

  return (
    <div className="page">
      <div className="content">
        <header className="brand">
          <img className="brand__icon" src={assetUrl('img/app-icon.png')} alt={app.name} />
          <h1 className="brand__name">{app.name}</h1>
          <p className="brand__slogan">{app.slogan}</p>
          <div className="brand__meta">
            <span>v{app.version}</span>
            <span className="brand__dot" aria-hidden="true" />
            <span>{ios ? 'iOS' : android ? 'Android' : '全平台'}</span>
          </div>
        </header>

        {notice}

        {share ? <ShareCard share={share} /> : null}

        <section className="section">
          <h2 className="section__title">
            下载安装
            {android && preferredStore ? (
              <span className="section__title-note">已为你的手机推荐</span>
            ) : null}
          </h2>

          {primaryAction}

          {gridItems.length > 0 ? (
            <StoreGrid
              items={gridItems}
              preferredId={android ? preferredStore?.id : undefined}
              onSelect={handleOpenStore}
            />
          ) : null}
        </section>
      </div>

      <footer className="footer">
        <span>
          {app.name} · {app.slogan}
        </span>
        <span className="footer__host">{SITE_HOST}</span>
      </footer>

      {guideOpen ? (
        <BrowserGuide
          browserName={browser.name}
          downloadUrl={android ? storeWebUrl(androidStores.find((s) => s.id === 'tencent')) : ''}
          onCopy={handleCopyLink}
          onClose={() => setGuideOpen(false)}
        />
      ) : null}

      <Toast message={toast} />
    </div>
  );
}
